/* bookable — home-checklist.js
   Scroll-driven check-off for the home checklist + the intro-call scheduler
   modal (availability stubbed client-side). Respects prefers-reduced-motion. */

(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- scroll-driven check-off ---------------- */
  document.querySelectorAll('.checklist__list').forEach((list) => {
    const items = Array.from(list.querySelectorAll('.checklist__item'));
    if (!items.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-checked'));
      return;
    }

    // Trigger when an item's top crosses ~58% of the viewport, so each row
    // checks itself off as it rises into the middle of the screen.
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-checked');
          io.unobserve(entry.target);
        }
      }
    }, { rootMargin: '0px 0px -42% 0px', threshold: 0 });

    items.forEach((item) => io.observe(item));
  });

  /* ---------------- intro-call scheduler modal ---------------- */
  document.querySelectorAll('.section-home-checklist').forEach((root) => {
    const modal = root.querySelector('[data-demo-modal]');
    const openBtn = root.querySelector('[data-demo-open]');
    if (!modal || !openBtn) return;

    const dialog = modal.querySelector('.demo-modal__dialog');
    const daysEl = modal.querySelector('[data-demo-days]');
    const slotsEl = modal.querySelector('[data-demo-slots]');
    const tzEl = modal.querySelector('[data-demo-tz]');
    const form = modal.querySelector('[data-demo-form]');
    const summary = modal.querySelector('[data-demo-summary]');
    const submit = modal.querySelector('[data-demo-submit]');
    const bookScene = modal.querySelector('[data-demo-scene="book"]');
    const confirmScene = modal.querySelector('[data-demo-scene="confirm"]');
    const confirmDetail = modal.querySelector('[data-demo-confirm-detail]');

    const TIMES = ['9:00 AM', '10:30 AM', '12:00 PM', '1:30 PM', '3:00 PM', '4:30 PM'];
    let selected = null; // { dayLabel, time }
    let lastFocused = null;

    // ---- stubbed availability: the next 5 weekdays, a handful of slots each.
    const buildDays = () => {
      const out = [];
      const cursor = new Date();
      cursor.setHours(0, 0, 0, 0);
      while (out.length < 5) {
        cursor.setDate(cursor.getDate() + 1);
        const dow = cursor.getDay();
        if (dow === 0 || dow === 6) continue; // skip weekends
        out.push(new Date(cursor));
      }
      return out;
    };

    const fmtDay = (d) =>
      d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

    // Deterministic stub: hide ~1 in 4 slots so availability feels real.
    const isOpen = (dayIndex, timeIndex) => (dayIndex * 3 + timeIndex) % 4 !== 0;

    const updateSummary = () => {
      const valid = !!selected;
      submit.disabled = !valid;
      summary.textContent = valid
        ? `intro call · ${selected.dayLabel} at ${selected.time}`
        : 'select a day and time to continue.';
      summary.classList.toggle('is-set', valid);
    };

    const renderSlots = (dayIndex, dayLabel) => {
      slotsEl.innerHTML = '';
      selected = null;
      updateSummary();
      TIMES.forEach((time, timeIndex) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'demo-slot';
        btn.textContent = time;
        if (!isOpen(dayIndex, timeIndex)) {
          btn.disabled = true;
          btn.classList.add('is-gone');
          btn.setAttribute('aria-label', `${time} — unavailable`);
        } else {
          btn.addEventListener('click', () => {
            slotsEl.querySelectorAll('.demo-slot').forEach((b) => b.classList.remove('is-active'));
            btn.classList.add('is-active');
            selected = { dayLabel, time };
            updateSummary();
          });
        }
        slotsEl.appendChild(btn);
      });
    };

    const renderDays = () => {
      const days = buildDays();
      daysEl.innerHTML = '';
      days.forEach((date, dayIndex) => {
        const label = fmtDay(date);
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'demo-day';
        btn.innerHTML =
          `<span class="demo-day__dow">${date.toLocaleDateString(undefined, { weekday: 'short' })}</span>` +
          `<span class="demo-day__date">${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>`;
        btn.addEventListener('click', () => {
          daysEl.querySelectorAll('.demo-day').forEach((b) => b.classList.remove('is-active'));
          btn.classList.add('is-active');
          renderSlots(dayIndex, label);
        });
        daysEl.appendChild(btn);
        if (dayIndex === 0) btn.click(); // open on the first available day
      });
      if (tzEl) {
        try {
          tzEl.textContent = `(${Intl.DateTimeFormat().resolvedOptions().timeZone})`;
        } catch (_) { /* no-op */ }
      }
    };

    // ---- focus trap ----
    const focusable = () =>
      Array.from(
        dialog.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null);

    const onKeydown = (e) => {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    };

    const open = () => {
      lastFocused = document.activeElement;
      // reset to the booking scene each open
      confirmScene.hidden = true;
      bookScene.hidden = false;
      if (!daysEl.children.length) renderDays();
      modal.hidden = false;
      document.body.classList.add('demo-modal-open');
      requestAnimationFrame(() => modal.classList.add('is-open'));
      document.addEventListener('keydown', onKeydown);
      (dialog || modal).focus();
    };

    const close = () => {
      modal.classList.remove('is-open');
      document.removeEventListener('keydown', onKeydown);
      document.body.classList.remove('demo-modal-open');
      const finish = () => { modal.hidden = true; };
      if (reduced) finish();
      else setTimeout(finish, 220);
      if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    };

    openBtn.addEventListener('click', open);
    modal.querySelectorAll('[data-demo-close]').forEach((el) => el.addEventListener('click', close));

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      if (!selected) { updateSummary(); return; }
      if (!form.reportValidity()) return;
      const name = form.elements.name.value.trim();
      confirmDetail.innerHTML =
        `<strong>${selected.dayLabel}</strong> at <strong>${selected.time}</strong>` +
        (name ? `<br>booked for ${name.replace(/</g, '&lt;')}` : '');
      bookScene.hidden = true;
      confirmScene.hidden = false;
      (dialog || modal).focus();
    });
  });
})();
